import { useCallback, useEffect, useState } from 'react';
import { Bot, AlertTriangle, Check } from '../../icons';
import { Button, EmptyState } from '../../components/shared';
import {
  getCompanyAi, setCompanyAi, type AiSurface, type SurfaceState,
} from '../../api/company-ai';
import { CompanyAiUsage } from './CompanyAiUsage';
import { CompanyAiQuota } from './CompanyAiQuota';
import './CompanyAi.css';

/**
 * 통합설정 › 회사 관리 › **AI 연결** (판 H-③).
 *
 * ★ 이름은 관리 콘솔(8444)의 「AI 연결」과 **같다** — 시스템 관리자가 거기서 만들어 «열고»,
 *   회사 관리자는 여기서 열린 것을 «고른다». 같은 것을 두 이름으로 부르면 어느 화면 이야기인지
 *   물어봐야 한다(2026-09-10 사용자 지시).
 *
 * ## 무엇을 고르는 화면인가
 *
 * 회사가 **AI 메뉴**와 **챗**에서 각각 **어느 모델들을** 쓸지 고르고, 그중 **기본 하나**를 정한다.
 *
 * ★★★ **판 K — 하나가 아니라 여럿이다.** 하나만 고르면 사용자가 대화에서 고를 것이 없다
 *   (2026-09-10 사용자 지적: 「대화에서 모델을 선택할 수 없다」). 여기서 여럿을 열어 주면
 *   사용자는 그 안에서 대화마다 고르고, 안 고르면 기본으로 간다.
 * 고를 수 있는 것은 **시스템 관리자가 관리 콘솔(8444)에서 «연» 목록**뿐이다 —
 * 주소·키는 여기 오지도 않고, 회사가 임의의 provider 를 지정할 수도 없다.
 *
 * ## ★★★ 세 가지를 가려서 말한다
 *
 *   목록을 **못 받아 왔다**   → 「지금 목록을 불러오지 못했습니다」 (다시 시도)
 *   열린 것이 **하나도 없다** → 「운영자가 아직 아무것도 열지 않았습니다」
 *   **안 골랐다**             → 「운영자 기본을 따릅니다」
 *
 * 셋을 한 화면(빈 목록)으로 접으면 관리자가 **무엇을 해야 하는지 알 수 없다.**
 *
 * ## ★★ 고른 것이 «닫혔을» 때
 *
 * 운영자가 나중에 목록에서 닫으면 회사 선택은 **조용히 무효**가 된다.
 * 그 사실을 화면이 말해야 「분명 골랐는데 왜 안 되지」가 안 생긴다.
 */

const SURFACE_LABEL: Record<AiSurface, string> = {
  'ai-menu': 'AI 메뉴',
  chat: '챗',
};

const SURFACE_HINT: Record<AiSurface, string> = {
  'ai-menu': 'AI 메뉴에서 묻고 답할 때 쓰는 모델',
  chat: '채팅에서 AI 를 부를 때 쓰는 모델',
};



export function CompanyAiSection(): JSX.Element {
  const [surfaces, setSurfaces] = useState<SurfaceState[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState<AiSurface | null>(null);
  const [savedAt, setSavedAt] = useState<AiSurface | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      setSurfaces(await getCompanyAi());
    } catch {
      setSurfaces(null);
      setLoadError(true);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const commit = useCallback(async (surface: AiSurface, keys: string[], defaultKey: string | null) => {
    setSaving(surface);
    setSaveError(null);
    try {
      await setCompanyAi(surface, keys, defaultKey);
      /* ★ 저장 뒤 다시 읽는다 — 서버가 막았을 수도 있고, 그 사이 운영자가 닫았을 수도 있다. */
      setSurfaces(await getCompanyAi());
      setSavedAt(surface);
      window.setTimeout(() => setSavedAt((s) => (s === surface ? null : s)), 2500);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSaveError(msg ?? '저장하지 못했습니다. 잠시 후 다시 시도해 주세요.');
      void load();
    } finally {
      setSaving(null);
    }
  }, [load]);

  if (loadError) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="설정을 불러오지 못했습니다"
        description="잠시 후 다시 시도해 주세요."
        action={<Button variant="secondary" onClick={() => void load()}>다시 시도</Button>}
      />
    );
  }

  if (surfaces === null) {
    return <div className="svc-cat__hint">불러오는 중…</div>;
  }

  return (
    <div className="company-ai">
      <p className="svc-cat__hint">
        AI 메뉴와 챗에서 쓸 모델을 고릅니다. 고를 수 있는 목록은 <b>시스템 관리자가 관리 콘솔에서
        열어 둔 것</b>이며, 아무것도 안 고르면 <b>운영자 기본</b>을 따릅니다.
        <br />
        <b>여럿</b>을 고를 수 있고, 그중 <b>기본</b> 하나를 정합니다 — 사용자는 대화에서 그 안에서
        고르고, 안 고르면 기본으로 답합니다.
      </p>

      {saveError !== null && (
        <div className="admin-console__alert" role="alert">
          <AlertTriangle size={16} aria-hidden="true" />
          <span>{saveError}</span>
        </div>
      )}

      {surfaces.map((s) => {
        const unavailable = s.available === null;
        const empty = s.available !== null && s.available.length === 0;
        /* 고른 것 중 «닫힌» 것 — 운영자가 나중에 닫으면 조용히 무효가 된다. */
        const closed = s.selected.filter((p) => p.stillOpen === false);
        const picked = new Set(s.selected.map((p) => p.key));
        const busy = saving === s.surface;

        const toggle = (key: string): void => {
          const next = picked.has(key)
            ? [...picked].filter((k) => k !== key)
            : [...picked, key];
          /* 기본이 빠지면 서버가 첫 번째로 바로잡는다 — 화면이 지어내지 않는다. */
          const nextDefault = next.includes(s.defaultKey ?? '') ? s.defaultKey : null;
          void commit(s.surface, next, nextDefault);
        };

        return (
          <section key={s.surface} className="company-ai__row">
            <h4 className="company-ai__ttl">{SURFACE_LABEL[s.surface]}</h4>
            <p className="company-ai__hint">{SURFACE_HINT[s.surface]}</p>

            {/* ★ 세 가지를 가려서 말한다 — 접으면 무엇을 해야 할지 알 수 없다. */}
            {unavailable && (
              <p className="company-ai__note company-ai__note--warn">
                <AlertTriangle size={14} aria-hidden="true" />
                지금 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
              </p>
            )}
            {empty && (
              <p className="company-ai__note">
                시스템 관리자가 아직 이 자리에 아무것도 열지 않았습니다.
              </p>
            )}

            {!unavailable && !empty && (
              <ul className="company-ai__list">
                {(s.available ?? []).map((c) => {
                  const on = picked.has(c.key);
                  const isDefault = s.defaultKey === c.key;
                  return (
                    <li key={c.key} className="company-ai__item">
                      <label className="company-ai__pick">
                        <input
                          type="checkbox"
                          checked={on}
                          disabled={busy}
                          onChange={() => toggle(c.key)}
                          aria-label={`${SURFACE_LABEL[s.surface]}에서 ${c.label} 쓰기`}
                        />
                        <span>{c.label}</span>
                      </label>
                      {/* 기본은 «고른 것 중»에서만 정한다 — 안 고른 것을 기본으로 두면 갈 곳이 없다. */}
                      {on && (
                        <button
                          type="button"
                          className={`company-ai__def${isDefault ? ' is-on' : ''}`}
                          aria-pressed={isDefault}
                          disabled={busy || isDefault}
                          onClick={() => void commit(s.surface, [...picked], c.key)}
                        >
                          {isDefault ? '기본' : '기본으로'}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}

            {closed.length > 0 && (
              <p className="company-ai__note company-ai__note--warn">
                <AlertTriangle size={14} aria-hidden="true" />
                전에 고른 항목 {closed.length}건은 <b>더 이상 쓸 수 없습니다</b>(운영자가 닫았습니다).
              </p>
            )}
            {picked.size === 0 && !unavailable && !empty && (
              <p className="company-ai__note">아무것도 안 골랐습니다 — 운영자 기본을 따릅니다.</p>
            )}
            {savedAt === s.surface && (
              <p className="company-ai__note company-ai__note--ok">
                <Check size={14} aria-hidden="true" />
                저장했습니다.
              </p>
            )}
          </section>
        );
      })}

      {/* 판 M — 「회사 관리자는 사원별 기본 토큰과 사원별 토큰을 관리해야 한다」(사용자 지시). */}
      <CompanyAiQuota />

      {/* 판 L — 「회사의 총사용량은 통합설정에서 AI 연결 메뉴에 나왔으면 한다」(사용자 지시). */}
      <CompanyAiUsage />

      <p className="svc-cat__hint company-ai__foot">
        <Bot size={14} aria-hidden="true" />
        모델을 바꿔도 <b>사람마다 보이는 데이터는 달라지지 않습니다</b> — AI 도구는 언제나
        묻는 사람 명의로 동작합니다.
      </p>
    </div>
  );
}
